import {
  Activity,
  ArrowLeft,
  Ban,
  Copy,
  FileText,
  Gauge as GaugeIcon,
  KeyRound,
  Plus,
  Settings2,
  Trash2,
  Users,
} from 'lucide-react';
import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Gauge } from '@/components/charts';
import { PeerConfigDialog } from '@/components/PeerConfigDialog';
import { InstanceStatusBadge, PeerStatusBadge } from '@/components/StatusBadge';
import { TrafficChart } from '@/components/TrafficChart';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  CardSubtitle,
  CardTitle,
  Checkbox,
  DataTable,
  EmptyState,
  FormField,
  Input,
  Modal,
  PageHeader,
  Skeleton,
  StatCard,
  Tabs,
  type DataTableColumn,
} from '@/components/ui';
import { useLiveInstance } from '@/hooks/LiveProvider';
import { useNotify } from '@/hooks/useNotify';
import { useT } from '@/i18n';
import { api, type Instance, type Peer, type TrafficPoint } from '@/lib/api';
import {
  copyToClipboard,
  formatBitrate,
  formatBytes,
  formatDateTime,
  formatPlanBandwidth,
  formatPrice,
  formatRelative,
  truncateKey,
} from '@/lib/format';
import { paths } from '@/router/paths';

export function InstanceDetailPage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const notify = useNotify();
  const t = useT();

  const [instance, setInstance] = useState<Instance | null>(null);
  const [peers, setPeers] = useState<Peer[]>([]);
  const [stats, setStats] = useState<TrafficPoint[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [createOpen, setCreateOpen] = useState(false);
  const [configPeer, setConfigPeer] = useState<Peer | null>(null);
  const [freshConfig, setFreshConfig] = useState<string | null>(null);
  const [rotateTarget, setRotateTarget] = useState<Peer | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Peer | null>(null);
  const [deleteInstanceOpen, setDeleteInstanceOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const live = useLiveInstance(id);

  const load = useCallback(async () => {
    try {
      const [detail, history] = await Promise.all([
        api.instances.get(id),
        api.instances.stats(id, 24),
      ]);
      setInstance(detail.instance);
      setPeers(detail.peers);
      setStats(history.stats);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Instanz konnte nicht geladen werden');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  // Historie im Hintergrund nachziehen, solange die Seite offen ist
  useEffect(() => {
    const timer = window.setInterval(() => {
      api.instances
        .stats(id, 24)
        .then(({ stats: points }) => setStats(points))
        .catch(() => undefined);
    }, 30_000);
    return () => window.clearInterval(timer);
  }, [id]);

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-24" />
        <Skeleton className="h-[132px]" />
        <Skeleton className="h-[380px]" />
      </div>
    );
  }

  if (!instance) {
    return (
      <EmptyState
        icon="⚠️"
        title="Instanz nicht gefunden"
        description={error ?? 'Die Instanz existiert nicht oder gehoert zu einem anderen Konto.'}
        action={<Button onClick={() => navigate(paths.instances.list)}>Zurueck zur Liste</Button>}
      />
    );
  }

  // Live-Zustand der Peers aus dem SSE-Stream anreichern
  const peersWithLive = peers.map((peer) => {
    const livePeer = live?.peers.find((entry) => entry.peerId === peer.id);
    return livePeer
      ? {
          ...peer,
          online: livePeer.online,
          endpoint: livePeer.endpoint,
          lastHandshakeAt: livePeer.lastHandshakeAt,
          rxBytes: livePeer.rxBytes,
          txBytes: livePeer.txBytes,
        }
      : peer;
  });

  const handleDeletePeer = async () => {
    if (!deleteTarget) return;
    setBusy(true);
    try {
      await api.instances.peers.remove(id, deleteTarget.id);
      notify.success(t('peers.removed', { name: deleteTarget.name }));
      setDeleteTarget(null);
      await load();
    } catch (cause) {
      notify.fromError(cause, t('errors.deleteFailed'));
    } finally {
      setBusy(false);
    }
  };

  /** Erzeugt ein neues Schluesselpaar und zeigt die Konfiguration einmalig. */
  const handleRotate = async (peer: Peer) => {
    setBusy(true);
    try {
      const { peer: updated, config } = await api.instances.peers.rotate(id, peer.id);
      setRotateTarget(null);
      setFreshConfig(config);
      setConfigPeer(updated);
      notify.success(t('peers.rotated'));
      await load();
    } catch (cause) {
      notify.fromError(cause, t('errors.generic'));
    } finally {
      setBusy(false);
    }
  };

  const handleToggle = async (peer: Peer) => {
    try {
      await api.instances.peers.update(id, peer.id, { enabled: !peer.enabled });
      notify.success(
        peer.enabled
          ? t('peers.disabledToast', { name: peer.name })
          : t('peers.enabled', { name: peer.name }),
      );
      await load();
    } catch (cause) {
      notify.fromError(cause, t('errors.generic'));
    }
  };

  const handleDeleteInstance = async () => {
    setBusy(true);
    try {
      await api.instances.remove(id);
      notify.success(t('instances.deleted'));
      navigate(paths.instances.list);
    } catch (cause) {
      notify.fromError(cause, t('errors.deleteFailed'));
      setBusy(false);
    }
  };

  const peerColumns: DataTableColumn<Peer>[] = [
    {
      key: 'name',
      header: 'Name',
      render: (peer) => (
        <div className="min-w-0">
          <p className="truncate font-bold text-t0">{peer.name}</p>
          <p className="truncate font-mono text-[11.5px] text-t2" title={peer.publicKey}>
            {truncateKey(peer.publicKey)}
          </p>
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (peer) => <PeerStatusBadge online={peer.online} enabled={peer.enabled} />,
    },
    {
      key: 'addresses',
      header: 'Adressen',
      hideBelow: 'md',
      render: (peer) => (
        <div className="font-mono text-[11.5px]">
          <p className="text-t1">{peer.ipv4Address}</p>
          {peer.ipv6Address && <p className="text-t2">{peer.ipv6Address}</p>}
        </div>
      ),
    },
    {
      key: 'handshake',
      header: 'Letzter Handshake',
      hideBelow: 'lg',
      render: (peer) => (
        <div className="text-[12px]">
          <p className="text-t1">{formatRelative(peer.lastHandshakeAt)}</p>
          {peer.endpoint && <p className="font-mono text-[11px] text-t2">{peer.endpoint}</p>}
        </div>
      ),
    },
    {
      key: 'traffic',
      header: 'Verkehr',
      hideBelow: 'md',
      align: 'right',
      render: (peer) => (
        <div className="font-mono text-[11.5px] tabular-nums">
          <p className="text-t1">{formatBytes(peer.rxBytes)} ein</p>
          <p className="text-t2">{formatBytes(peer.txBytes)} aus</p>
        </div>
      ),
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      width: '160px',
      render: (peer) => (
        <div className="flex justify-end gap-1" onClick={(event) => event.stopPropagation()}>
          {/* Laedt die Konfiguration nach - ohne privaten Schluessel, den gibt
              es nur einmal beim Anlegen. */}
          <IconAction
            title={t('peers.reopenConfig')}
            onClick={() => {
              setFreshConfig(null);
              setConfigPeer(peer);
            }}
          >
            <FileText size={15} />
          </IconAction>
          <IconAction title={t('peers.rotate')} onClick={() => setRotateTarget(peer)}>
            <KeyRound size={15} />
          </IconAction>
          <IconAction
            title={peer.enabled ? t('peers.toggleDisable') : t('peers.toggleEnable')}
            onClick={() => void handleToggle(peer)}
          >
            <Ban size={15} className={peer.enabled ? '' : 'text-warn'} />
          </IconAction>
          <IconAction title={t('common.delete')} danger onClick={() => setDeleteTarget(peer)}>
            <Trash2 size={15} />
          </IconAction>
        </div>
      ),
    },
  ];

  return (
    <div>
      <button
        type="button"
        onClick={() => navigate(paths.instances.list)}
        className="mb-2 inline-flex items-center gap-1.5 text-[12px] font-semibold text-t2 hover:text-t0"
      >
        <ArrowLeft size={14} />
        Alle Instanzen
      </button>

      <PageHeader
        title={instance.name}
        subtitle={`${instance.ifName} · ${instance.endpoint} · ${instance.server.name}${
          instance.server.location ? ` (${instance.server.location})` : ''
        }`}
        actions={
          <>
            <InstanceStatusBadge status={instance.status} />
            {instance.isDedicated && instance.dedicatedIp && (
              <Badge variant="accent">
                {t('instances.dedicated')} · {instance.dedicatedIp}
              </Badge>
            )}
            <Button variant="danger" icon={<Trash2 size={15} />} onClick={() => setDeleteInstanceOpen(true)}>
              Instanz loeschen
            </Button>
          </>
        }
      />

      {instance.statusMessage && instance.status === 'ERROR' && (
        <div className="mb-5 rounded-[var(--radius-vela-md)] border border-line bg-bad-soft px-4 py-3 text-[13px] font-medium text-bad">
          {instance.statusMessage}
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard
          label="Peers"
          value={`${instance.peerCount} / ${instance.maxPeers}`}
          icon={<Users size={20} />}
          delta={{ value: `${live?.onlinePeers ?? 0} online`, positive: true }}
        />
        <StatCard
          label="Uplink"
          value={formatPlanBandwidth(instance.bandwidthLimitMbps)}
          icon={<GaugeIcon size={20} />}
          iconColor="var(--info)"
          iconBg="var(--info-soft)"
        />
        <StatCard
          label="Eingehend"
          value={formatBitrate(live?.rxBitsPerSecond ?? 0)}
          icon={<Activity size={20} />}
          iconColor="var(--ok)"
          iconBg="var(--ok-soft)"
        />
        <StatCard
          label="Ausgehend"
          value={formatBitrate(live?.txBitsPerSecond ?? 0)}
          icon={<Activity size={20} />}
          iconColor="var(--warn)"
          iconBg="var(--warn-soft)"
        />
      </div>

      <div className="mt-5">
        <Tabs
          items={[
            {
              key: 'peers',
              label: `Peers (${instance.peerCount})`,
              content: (
                <Card padding="none">
                  <div className="flex items-center justify-between gap-3 border-b border-line px-5 py-4">
                    <div>
                      <CardTitle>Peers</CardTitle>
                      <CardSubtitle>
                        {instance.peerCount} von {instance.maxPeers} Plaetzen belegt
                      </CardSubtitle>
                    </div>
                    <Button
                      icon={<Plus size={15} />}
                      disabled={instance.peerCount >= instance.maxPeers || instance.status !== 'ACTIVE'}
                      onClick={() => setCreateOpen(true)}
                    >
                      Peer anlegen
                    </Button>
                  </div>

                  {peersWithLive.length === 0 ? (
                    <div className="p-5">
                      <EmptyState
                        icon="👥"
                        title="Noch keine Peers"
                        description="Legen Sie den ersten Peer an - Schluessel, IP-Adresse und QR-Code werden automatisch erzeugt."
                      />
                    </div>
                  ) : (
                    <DataTable columns={peerColumns} data={peersWithLive} rowKey={(peer) => peer.id} />
                  )}
                </Card>
              ),
            },
            {
              key: 'traffic',
              label: 'Datenverkehr',
              content: (
                <div className="grid gap-4 lg:grid-cols-3">
                  <Card className="lg:col-span-2">
                    <CardHeader>
                      <div>
                        <CardTitle>Verlauf der letzten 24 Stunden</CardTitle>
                        <CardSubtitle>Gruen: Upload der Clients, Blau: Download der Clients</CardSubtitle>
                      </div>
                    </CardHeader>
                    <TrafficChart points={stats} direction="rx" height={200} />
                    <div className="mt-4 border-t border-line pt-4">
                      <TrafficChart points={stats} direction="tx" height={160} />
                    </div>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle>Auslastung</CardTitle>
                    </CardHeader>
                    <div className="flex flex-col items-center">
                      <Gauge
                        value={live?.utilizationPercent ?? 0}
                        max={100}
                        label={`${live?.utilizationPercent ?? 0}% vom Tariflimit`}
                        color={
                          (live?.utilizationPercent ?? 0) > 90
                            ? 'var(--bad)'
                            : (live?.utilizationPercent ?? 0) > 70
                              ? 'var(--warn)'
                              : 'var(--acc)'
                        }
                      />
                    </div>
                    <dl className="mt-5 space-y-3 border-t border-line pt-4 text-[13px]">
                      <div className="flex justify-between">
                        <dt className="text-t2">Gesamt empfangen</dt>
                        <dd className="font-mono text-t0 tabular-nums">{formatBytes(live?.rxBytes ?? 0)}</dd>
                      </div>
                      <div className="flex justify-between">
                        <dt className="text-t2">Gesamt gesendet</dt>
                        <dd className="font-mono text-t0 tabular-nums">{formatBytes(live?.txBytes ?? 0)}</dd>
                      </div>
                      <div className="flex justify-between">
                        <dt className="text-t2">Tariflimit</dt>
                        <dd className="font-mono text-t0">
                          {formatPlanBandwidth(instance.bandwidthLimitMbps)}
                        </dd>
                      </div>
                    </dl>
                  </Card>
                </div>
              ),
            },
            {
              key: 'settings',
              label: 'Einstellungen',
              content: <SettingsTab instance={instance} onChanged={load} />,
            },
          ]}
        />
      </div>

      <CreatePeerDialog
        open={createOpen}
        instance={instance}
        onClose={() => setCreateOpen(false)}
        onCreated={async (peer, config) => {
          setCreateOpen(false);
          notify.success(t('peers.created', { name: peer.name }));
          await load();
          setFreshConfig(config);
          setConfigPeer(peer);
        }}
      />

      <PeerConfigDialog
        open={Boolean(configPeer)}
        onClose={() => {
          setConfigPeer(null);
          setFreshConfig(null);
        }}
        instanceId={id}
        peer={configPeer}
        config={freshConfig}
        onRotate={configPeer ? () => void handleRotate(configPeer) : undefined}
      />

      <Modal
        open={Boolean(rotateTarget)}
        onClose={() => setRotateTarget(null)}
        title={t('peers.rotateTitle')}
        size="sm"
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setRotateTarget(null)} disabled={busy}>
              {t('common.cancel')}
            </Button>
            <Button
              onClick={() => rotateTarget && void handleRotate(rotateTarget)}
              disabled={busy}
              icon={<KeyRound size={15} />}
            >
              {t('peers.rotateConfirm')}
            </Button>
          </div>
        }
      >
        <p className="text-[13px] text-t1">
          {t('peers.rotateText', { name: rotateTarget?.name ?? '' })}
        </p>
      </Modal>

      <Modal
        open={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        title="Peer loeschen"
        size="sm"
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setDeleteTarget(null)} disabled={busy}>
              Abbrechen
            </Button>
            <Button variant="danger" onClick={handleDeletePeer} disabled={busy}>
              Loeschen
            </Button>
          </div>
        }
      >
        <p className="text-[13px] text-t1">
          Soll "{deleteTarget?.name}" dauerhaft entfernt werden? Das Geraet verliert sofort den
          Zugang; alle anderen Verbindungen bleiben bestehen.
        </p>
      </Modal>

      <Modal
        open={deleteInstanceOpen}
        onClose={() => setDeleteInstanceOpen(false)}
        title="Instanz loeschen"
        size="sm"
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setDeleteInstanceOpen(false)} disabled={busy}>
              Abbrechen
            </Button>
            <Button variant="danger" onClick={handleDeleteInstance} disabled={busy}>
              Endgueltig loeschen
            </Button>
          </div>
        }
      >
        <p className="text-[13px] text-t1">
          Die Instanz "{instance.name}" wird mit allen {instance.peerCount} Peers entfernt.
          Interface, Bandbreitenregeln und Firewall-Eintraege werden abgebaut. Dieser Schritt kann
          nicht rueckgaengig gemacht werden.
        </p>
      </Modal>
    </div>
  );
}

function IconAction({
  title,
  onClick,
  danger,
  children,
}: {
  title: string;
  onClick: () => void;
  danger?: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      title={title}
      aria-label={title}
      onClick={onClick}
      className={`flex h-8 w-8 items-center justify-center rounded-[var(--radius-vela-sm)] transition-colors ${
        danger ? 'text-bad hover:bg-bad-soft' : 'text-t1 hover:bg-bg-3 hover:text-t0'
      }`}
    >
      {children}
    </button>
  );
}

// ---------------------------------------------------------------------------

function CreatePeerDialog({
  open,
  instance,
  onClose,
  onCreated,
}: {
  open: boolean;
  instance: Instance;
  onClose: () => void;
  onCreated: (peer: Peer, config: string | null) => void | Promise<void>;
}) {
  const t = useT();

  // Gesamter Verkehr bzw. nur die Netze dieser Instanz (Split-Tunnel).
  const fullRoute = instance.ipv6Cidr ? '0.0.0.0/0, ::/0' : '0.0.0.0/0';
  const tunnelRoute = [instance.ipv4Cidr, instance.ipv6Cidr].filter(Boolean).join(', ');

  const [name, setName] = useState('');
  const [mode, setMode] = useState<'server' | 'own'>('server');
  const [publicKey, setPublicKey] = useState('');
  const [usePsk, setUsePsk] = useState(true);
  const [routing, setRouting] = useState<'full' | 'tunnel' | 'custom'>('full');
  const [allowedIps, setAllowedIps] = useState(fullRoute);
  const [siteNets, setSiteNets] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /** Bei den Vorgaben den Wert mitziehen; "custom" laesst ihn unangetastet. */
  const chooseRouting = (next: 'full' | 'tunnel' | 'custom') => {
    setRouting(next);
    if (next === 'full') setAllowedIps(fullRoute);
    if (next === 'tunnel') setAllowedIps(tunnelRoute);
  };

  useEffect(() => {
    if (open) {
      setName('');
      setPublicKey('');
      setError(null);
      setMode('server');
      setRouting('full');
      setAllowedIps(fullRoute);
    }
    // fullRoute haengt nur an der Instanz und aendert sich im offenen Dialog nicht
  }, [open, fullRoute]);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);

    try {
      const { peer, config } = await api.instances.peers.create(instance.id, {
        name: name.trim(),
        publicKey: mode === 'own' ? publicKey.trim() : undefined,
        usePresharedKey: usePsk,
        clientAllowedIps: allowedIps.trim(),
        extraAllowedIps: siteNets
          .split(',')
          .map((entry) => entry.trim())
          .filter(Boolean),
      });
      await onCreated(peer, config);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Peer konnte nicht angelegt werden');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title={t('peers.add')}>
      <form onSubmit={submit} className="space-y-4">
        <FormField label={t('peers.deviceName')} required>
          <Input
            required
            maxLength={80}
            placeholder={t('peers.devicePlaceholder')}
            value={name}
            onChange={(event) => setName(event.target.value)}
          />
        </FormField>

        <div>
          <p className="mb-1.5 text-[12.5px] font-semibold text-t1">{t('peers.keys')}</p>
          <div className="grid gap-2 sm:grid-cols-2">
            <ChoiceCard
              active={mode === 'server'}
              title={t('peers.keysServer')}
              description={t('peers.keysServerHint')}
              onClick={() => setMode('server')}
            />
            <ChoiceCard
              active={mode === 'own'}
              title={t('peers.keysOwn')}
              description={t('peers.keysOwnHint')}
              onClick={() => setMode('own')}
            />
          </div>
        </div>

        {mode === 'own' && (
          <FormField label={t('peers.publicKey')} required>
            <Input
              required
              className="font-mono text-xs"
              placeholder={t('peers.publicKeyPlaceholder')}
              value={publicKey}
              onChange={(event) => setPublicKey(event.target.value)}
            />
          </FormField>
        )}

        <div>
          <p className="mb-1.5 text-[12.5px] font-semibold text-t1">{t('peers.routing')}</p>
          <div className="grid gap-2 sm:grid-cols-3">
            <ChoiceCard
              active={routing === 'full'}
              title={t('peers.routingFull')}
              description={t('peers.routingFullHint')}
              onClick={() => chooseRouting('full')}
            />
            <ChoiceCard
              active={routing === 'tunnel'}
              title={t('peers.routingTunnel')}
              description={t('peers.routingTunnelHint')}
              onClick={() => chooseRouting('tunnel')}
            />
            <ChoiceCard
              active={routing === 'custom'}
              title={t('peers.routingCustom')}
              description={t('peers.routingCustomHint')}
              onClick={() => chooseRouting('custom')}
            />
          </div>
        </div>

        <FormField
          label={t('peers.clientAllowedIps')}
          hint={routing === 'custom' ? t('peers.clientAllowedIpsHint') : undefined}
        >
          <Input
            className="font-mono text-xs"
            // Bei den Vorgaben nur anzeigen - geaendert wird ueber die Auswahl.
            readOnly={routing !== 'custom'}
            value={allowedIps}
            onChange={(event) => setAllowedIps(event.target.value)}
          />
        </FormField>

        {instance.allowSiteToSite && (
          <FormField label={t('peers.behindPeer')} hint={t('peers.behindPeerHint')}>
            <Input
              className="font-mono text-xs"
              placeholder="192.168.30.0/24, 10.20.0.0/16"
              value={siteNets}
              onChange={(event) => setSiteNets(event.target.value)}
            />
          </FormField>
        )}

        <Checkbox
          checked={usePsk}
          onChange={(event) => setUsePsk(event.target.checked)}
          label={t('peers.usePsk')}
        />

        {error && (
          <div className="rounded-[var(--radius-vela-md)] border border-line bg-bad-soft px-3 py-2 text-[13px] text-bad">
            {error}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-1">
          <Button type="button" variant="secondary" onClick={onClose} disabled={busy}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" disabled={busy}>
            {t('peers.add')}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

function ChoiceCard({
  active,
  title,
  description,
  onClick,
}: {
  active: boolean;
  title: string;
  description: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-[var(--radius-vela-md)] border px-3 py-2.5 text-left transition-colors ${
        active ? 'border-acc bg-acc-soft' : 'border-line bg-bg-inset hover:border-line-2'
      }`}
    >
      <span className="block text-[13px] font-bold text-t0">{title}</span>
      <span className="mt-0.5 block text-[11.5px] text-t2">{description}</span>
    </button>
  );
}

// ---------------------------------------------------------------------------

function SettingsTab({ instance, onChanged }: { instance: Instance; onChanged: () => Promise<void> }) {
  const notify = useNotify();
  const t = useT();
  const [name, setName] = useState(instance.name);
  const [dns, setDns] = useState(instance.dnsServers);
  const [mtu, setMtu] = useState(instance.mtu);
  const [siteNets, setSiteNets] = useState(instance.siteToSiteNets.join(', '));
  const [busy, setBusy] = useState(false);

  const bandwidthOptions = [
    { mbps: instance.plan.baseBandwidthMbps, priceCents: 0 },
    ...instance.plan.bandwidthTiers,
  ];

  /**
   * Neuer Monatspreis nach einem Stufenwechsel: eingefrorener Instanzpreis
   * minus aktueller Stufenaufpreis plus neuer. So bleibt eine zugebuchte
   * dedizierte IPv4 im Preis erhalten.
   */
  const currentTierCents =
    bandwidthOptions.find((option) => option.mbps === instance.bandwidthLimitMbps)?.priceCents ?? 0;
  const priceAfter = (option: { priceCents: number }) =>
    instance.priceCents - currentTierCents + option.priceCents;

  const [pendingBandwidth, setPendingBandwidth] = useState<{
    mbps: number;
    newPriceCents: number;
  } | null>(null);

  const save = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    try {
      await api.instances.update(instance.id, {
        name: name.trim(),
        dnsServers: dns.trim(),
        mtu,
        siteToSiteNets: instance.allowSiteToSite
          ? siteNets
              .split(',')
              .map((entry) => entry.trim())
              .filter(Boolean)
          : undefined,
      });
      notify.success(t('instances.settingsSaved'));
      await onChanged();
    } catch (cause) {
      notify.fromError(cause, t('errors.saveFailed'));
    } finally {
      setBusy(false);
    }
  };

  const changeBandwidth = async (mbps: number) => {
    setBusy(true);
    try {
      await api.instances.setBandwidth(instance.id, mbps);
      notify.success(t('instances.uplinkChanged', { value: formatPlanBandwidth(mbps) }));
      await onChanged();
    } catch (cause) {
      notify.fromError(cause, t('errors.generic'));
    } finally {
      setBusy(false);
      setPendingBandwidth(null);
    }
  };

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <Card>
        <CardHeader>
          <div>
            <CardTitle>{t('instances.settingsGeneral')}</CardTitle>
            <CardSubtitle>{t('instances.settingsGeneralHint')}</CardSubtitle>
          </div>
          <Settings2 size={17} className="text-t2" />
        </CardHeader>

        <form onSubmit={save} className="space-y-4">
          <FormField label="Bezeichnung">
            <Input value={name} onChange={(event) => setName(event.target.value)} />
          </FormField>
          <FormField label="DNS-Server der Clients">
            <Input
              className="font-mono text-xs"
              value={dns}
              onChange={(event) => setDns(event.target.value)}
            />
          </FormField>
          <FormField
            label="MTU"
            hint="1420 passt fuer die meisten Anschluesse. Niedrigere Werte helfen bei PPPoE-Leitungen."
          >
            <Input
              type="number"
              min={1280}
              max={1500}
              value={mtu}
              onChange={(event) => setMtu(Number(event.target.value))}
            />
          </FormField>

          {instance.allowSiteToSite && (
            <FormField
              label="Freigegebene Site-to-Site-Netze"
              hint="Nur diese privaten Netze duerfen ueber den Tunnel erreicht werden."
            >
              <Input
                className="font-mono text-xs"
                placeholder="192.168.0.0/16"
                value={siteNets}
                onChange={(event) => setSiteNets(event.target.value)}
              />
            </FormField>
          )}

          <Button type="submit" disabled={busy}>
            Speichern
          </Button>
        </form>
      </Card>

      <div className="space-y-4">
        <Card>
          <CardHeader>
            <div>
              <CardTitle>Uplink</CardTitle>
              <CardSubtitle>
                Aenderungen greifen sofort, bestehende Verbindungen bleiben bestehen.
              </CardSubtitle>
            </div>
            <GaugeIcon size={17} className="text-t2" />
          </CardHeader>

          <div className="space-y-2">
            {bandwidthOptions.map((option) => {
              const active = option.mbps === instance.bandwidthLimitMbps;
              return (
                <button
                  key={option.mbps}
                  type="button"
                  disabled={active}
                  // Erst die Preisabfrage - geaendert wird nach Bestaetigung.
                  onClick={() =>
                    setPendingBandwidth({ mbps: option.mbps, newPriceCents: priceAfter(option) })
                  }
                  className={`flex w-full items-center justify-between rounded-[var(--radius-vela-md)] border px-4 py-3 text-left transition-colors ${
                    active ? 'border-acc bg-acc-soft' : 'border-line bg-bg-inset hover:border-line-2'
                  }`}
                >
                  <span>
                    <span className="block text-[13px] font-bold text-t0">
                      {formatPlanBandwidth(option.mbps)}
                    </span>
                    <span className="mt-0.5 block text-[11.5px] text-t2">
                      {option.priceCents === 0
                        ? 'im Tarif enthalten'
                        : `+${formatPrice(option.priceCents, instance.plan.currency)} pro Monat`}
                    </span>
                  </span>
                  <span className="text-[11.5px] font-bold text-t2">
                    {active ? 'aktiv' : 'waehlen'}
                  </span>
                </button>
              );
            })}
          </div>
        </Card>

        <Card padding="none">
          <div className="border-b border-line px-5 py-4">
            <CardTitle>Verbindungsdaten</CardTitle>
          </div>
          <dl className="divide-y divide-line">
            <DetailRow label="Endpoint" value={instance.endpoint} copyable />
            <DetailRow label="Server Public Key" value={instance.publicKey} copyable mono />
            <DetailRow label="IPv4-Tunnelnetz" value={instance.ipv4Cidr} mono />
            <DetailRow label="IPv6-Praefix" value={instance.ipv6Cidr ?? 'nicht zugewiesen'} mono />
            <DetailRow
              label="Ausgehende IPv4"
              value={
                instance.ipMode === 'DEDICATED_IPV4'
                  ? `${instance.dedicatedIp} (dediziert)`
                  : 'geteilt (NAT)'
              }
            />
            <DetailRow label="Tarif" value={instance.plan.name} />
            <DetailRow label="Letzter Abgleich" value={formatDateTime(instance.lastSyncAt)} />
          </dl>
        </Card>
      </div>

      <Modal
        open={Boolean(pendingBandwidth)}
        onClose={() => setPendingBandwidth(null)}
        title={t('instances.uplinkConfirmTitle')}
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setPendingBandwidth(null)} disabled={busy}>
              {t('common.cancel')}
            </Button>
            <Button
              onClick={() => pendingBandwidth && void changeBandwidth(pendingBandwidth.mbps)}
              disabled={busy}
            >
              {t('instances.uplinkConfirmAction')}
            </Button>
          </div>
        }
      >
        {pendingBandwidth && (
          <div className="space-y-3">
            <p className="text-[13.5px] leading-relaxed text-t1">
              {t('instances.uplinkConfirmText', {
                bandwidth: formatPlanBandwidth(pendingBandwidth.mbps),
              })}
            </p>
            <div className="overflow-hidden rounded-[var(--radius-vela-md)] border border-line">
              <div className="flex items-center justify-between bg-bg-inset px-4 py-3 text-[13px]">
                <span className="text-t2">{t('instances.uplinkPriceCurrent')}</span>
                <span className="font-semibold tabular-nums text-t1">
                  {formatPrice(instance.priceCents, instance.plan.currency)}
                  {t('common.perMonth')}
                </span>
              </div>
              <div className="flex items-center justify-between border-t border-line bg-bg-inset px-4 py-3 text-[13px]">
                <span className="text-t2">{t('instances.uplinkPriceNew')}</span>
                <span
                  className={`font-bold tabular-nums ${
                    pendingBandwidth.newPriceCents > instance.priceCents ? 'text-warn' : 'text-ok'
                  }`}
                >
                  {formatPrice(pendingBandwidth.newPriceCents, instance.plan.currency)}
                  {t('common.perMonth')}
                </span>
              </div>
            </div>
            <p className="text-[12px] text-t2">{t('instances.uplinkPriceHint')}</p>
          </div>
        )}
      </Modal>
    </div>
  );
}

function DetailRow({
  label,
  value,
  copyable,
  mono,
}: {
  label: string;
  value: string;
  copyable?: boolean;
  mono?: boolean;
}) {
  const notify = useNotify();

  return (
    <div className="flex items-center justify-between gap-4 px-5 py-3">
      <dt className="shrink-0 text-[11.5px] text-t2">{label}</dt>
      <dd className={`flex min-w-0 items-center gap-2 ${mono ? 'font-mono text-[11.5px]' : 'text-[13px]'}`}>
        <span className="truncate text-t0" title={value}>
          {value}
        </span>
        {copyable && (
          <button
            type="button"
            className="shrink-0 text-t2 hover:text-t0"
            aria-label={`${label} kopieren`}
            onClick={async () => {
              const ok = await copyToClipboard(value);
              if (ok) notify.success(`${label} kopiert.`);
              else notify.error('Kopieren nicht moeglich.');
            }}
          >
            <Copy size={13} />
          </button>
        )}
      </dd>
    </div>
  );
}
